import {Component, OnInit} from '@angular/core';
import {CompanyHttpService} from "../x-http-requests/company-http.service";
import {CompanyDto} from "../../models/admin_owner/company/company-dto";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {AppRoutes} from "../../enums/routes";
import {AlertLevel} from "../../enums/alert-level";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {OwnerRoutes} from "../../_modules/owner/_models/_enums/owner-routes";
import {describeAdministrationError} from "../../_shared/errors/administration-errors";
import {LoadingComponent} from "../../_modules/x-components/loading/loading.component";
import {CommonModule} from "@angular/common";
import {CompanyContractsComponent} from "../../_modules/owner/company-contracts/company-contracts.component";

@Component({
  selector: 'app-company',
  templateUrl: './company.component.html',
  imports: [
    CommonModule,
    RouterLink,
    LoadingComponent,
    CompanyContractsComponent
  ],
  styleUrls: ['./company.component.scss']
})
export class OwnerCompanyComponent implements OnInit {
  companies?: CompanyDto[];
  filtered_companies?: CompanyDto[];
  searchText: string = "";
  name: string = "";
  // Toolbar state: status filter + sort key, both applied client-side in applyFilters().
  activeFilter: 'all' | 'active' | 'inactive' = 'all';
  sortKey: 'name' | 'newest' | 'modified' = 'name';
  showFilters: boolean = false; // filter/sort toolbar is collapsed by default
  contractsCompany?: CompanyDto; // company whose contract modal is open (undefined = closed)

  constructor(private companyHttpService: CompanyHttpService,
              private route: ActivatedRoute, private router: Router,
              private gas: GlobalAlertService) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  redirectToSettings(id: string) {
    this.router.navigate([`/${AppRoutes.OwnerModule}/${OwnerRoutes.CompanyCreate}`], {queryParams: {id: id}}).then()
  }

  RemoveCompany(company: CompanyDto): void {
    let confirmation = confirm("Are you sure you want to delete the following company:\n" + company.name + "\n" + company.id);
    if (!confirmation) return;
    this.companyHttpService.delete(company.id).subscribe(x => {
      this.companies!.splice(this.companies!.indexOf(company), 1);
      this.FilterItem(this.searchText);
    }, error => {
      console.error(error);
      // `CompanyService` registers no error codes at all, so this sentence is the entire message. It names the
      // two rules the server really enforces (own company, protected/initial company) rather than "try again",
      // which is the one thing that can never change the outcome here.
      this.fail(error, "Das Unternehmen konnte nicht gelöscht werden. "
        + "Ihr eigenes Unternehmen und geschützte Unternehmen bleiben bestehen.");
    });
  }

  AssignCopy() {
    this.applyFilters();
  }

  FilterItem(value: string) {
    this.searchText = value;
    this.applyFilters();
  }

  setActiveFilter(value: 'all' | 'active' | 'inactive') {
    this.activeFilter = value;
    this.applyFilters();
  }

  setSort(value: 'name' | 'newest' | 'modified') {
    this.sortKey = value;
    this.applyFilters();
  }

  /** Recomputes filtered_companies from the search text, active-state filter and sort key. */
  private applyFilters() {
    if (!this.companies) {
      this.filtered_companies = undefined;
      return;
    }
    const query = this.searchText.trim().toLowerCase();
    let list = this.companies.filter(c => !query || c.name.toLowerCase().includes(query));
    if (this.activeFilter === 'active') list = list.filter(c => c.active);
    else if (this.activeFilter === 'inactive') list = list.filter(c => !c.active);
    this.filtered_companies = list.sort((a, b) => {
      switch (this.sortKey) {
        case 'newest':
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        case 'modified':
          return new Date(b.modified).getTime() - new Date(a.modified).getTime();
        default:
          return a.name.localeCompare(b.name);
      }
    });
  }

  LoadData() {
    this.companyHttpService.list().subscribe(x => {
      this.companies = x;
      this.AssignCopy();
    }, error => console.error(error));
  }

  ngOnInit(): void {
    this.LoadData();
  }

  /**
   * Surfaces a refusal as a German sentence through the global alert banner.
   *
   * Replaces `ErrorBannerComponent`, which rendered `Error · {{ statusText }}`. Every failure this screen can
   * see is a bare status — `CompanyService` registers no `ErrorCatalog` codes — so the per-call-site sentence
   * IS the message here, by construction rather than by omission.
   */
  private fail(err: unknown, fallback: string): void {
    this.gas.createAndShow("Fehler", describeAdministrationError(err, fallback), AlertLevel.error, 8000);
  }
}
